(() => {
  'use strict';
  if (customElements.get('cs-men-gallery')) return;
  class CsMenGallery extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.items = [...this.querySelectorAll('template[data-men-media]')];
      this.stage = this.querySelector('[data-men-stage]');
      this.viewport = this.querySelector('[data-men-viewport]');
      this.rail = this.querySelector('[data-men-rail]');
      this.dialog = this.querySelector('[data-men-dialog]');
      this.thumbs = [...this.querySelectorAll('[data-men-select]')];
      this.teaser = this.querySelector('[data-men-teaser]');
      this.previewControl = this.querySelector('[data-men-preview-pause]');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection;
      this.index = 0;
      this.previewPaused = false;
      this.previewDenied = false;
      this.previewOptIn = false;
      this.previewVisible = false;
      this.previewPending = false;
      this.previewEpoch = 0;
      this.playEpoch = 0;
      this.playerStates = this.playerStates || new Map();
      this.systemPauses = new WeakSet();
      this.pageHidden = false;
      this.lastTrigger = null;
      this.setupImageLoading();
      this.observe();
      this.listen(this, 'click', event => this.onClick(event));
      this.listen(this, 'keydown', event => {
        if (event.target.closest('video,iframe,model-viewer,input,textarea,select,[contenteditable="true"]')) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.select(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      });
      this.listen(this, 'touchstart', event => {
        this.touchStart = event.target.closest('[data-men-photo]') && event.touches.length === 1 ? {x: event.touches[0].clientX, y: event.touches[0].clientY} : null;
      }, {passive: true});
      this.listen(this, 'touchcancel', () => { this.touchStart = null; }, {passive: true});
      this.listen(this, 'touchend', event => {
        if (!this.touchStart || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - this.touchStart.x;
        const dy = event.changedTouches[0].clientY - this.touchStart.y;
        this.touchStart = null;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.swipeUntil = Date.now() + 450;
          this.select(this.index + (dx < 0 ? 1 : -1));
        }
      }, {passive: true});
      this.listen(this.dialog, 'close', () => this.closeDialog());
      this.listen(document, 'visibilitychange', () => {
        if (document.hidden) { this.stopPlayer(); this.stopPreview(); }
        else { this.syncPlayer(true); this.syncPreview(); this.checkGalleryImages(); }
      });
      this.listen(window, 'scrollend', event => {
        if (event.target === document || event.target === window) this.finishSelectionScroll();
      });
      for (const event of ['wheel', 'touchstart']) this.listen(window, event, () => this.finishSelectionScroll(), {passive: true});
      this.listen(window, 'pagehide', () => {
        this.pageHidden = true; this.stopPlayer(); this.stopPreview();
        this.imageRetryTimers?.forEach(timer => clearTimeout(timer));
        this.imageRetryTimers?.clear();
        clearTimeout(this.prefetchTimer);
      });
      this.listen(window, 'pageshow', () => { this.pageHidden = false; this.syncPlayer(true); this.syncPreview(); this.checkGalleryImages(); });
      const refreshMedia = () => {
        if (this.mediaFrame) return;
        this.mediaFrame = requestAnimationFrame(() => { this.mediaFrame = null; this.syncPlayer(); });
      };
      this.listen(window, 'scroll', refreshMedia, {passive: true});
      this.listen(window, 'resize', refreshMedia);
      if (window.visualViewport) {
        this.listen(window.visualViewport, 'resize', refreshMedia);
        this.listen(window.visualViewport, 'scroll', refreshMedia, {passive: true});
      }
      this.listen(document, 'dialog:after-show', refreshMedia);
      this.listen(document, 'dialog:after-hide', refreshMedia);
      const preferenceChanged = () => { this.previewOptIn = false; this.playerStates.forEach(state => { state.started = false; }); this.syncPlayer(); this.syncPreview(); };
      this.listen(this.reduced, 'change', preferenceChanged);
      if (this.connection?.addEventListener) this.listen(this.connection, 'change', preferenceChanged);
      if (this.teaser) {
        this.listen(this.teaser, 'timeupdate', () => {
          if (this.teaser.currentTime >= 5) this.teaser.currentTime = 0;
        });
        this.listen(this.teaser, 'error', () => {
          this.previewDenied = true;
          this.stopPreview();
          this.updatePreviewControl();
        });
      } else if (this.previewControl) this.previewControl.hidden = true;
      if (this.items.length && (this.hasConnectedBefore || this.items[0].dataset.kind !== 'image')) this.render(false);
      this.syncPlayer();
      this.hasConnectedBefore = true;
      this.update();
      this.querySelectorAll('button[data-men-pending]').forEach(button => {
        button.disabled = button === this.previewControl && this.items[this.index]?.dataset.kind !== 'image';
        button.removeAttribute('data-men-pending');
      });
      this.dataset.menReady = 'true';
      this.checkGalleryImages();
    }
    setupImageLoading() {
      if (!this.hasAttribute('data-men-resilient')) return;
      this.imageStates = new WeakMap();
      this.imageRetryTimers = new Map();
      this.prefetched = new Set();
      this.listen(this, 'error', event => {
        if (event.target.matches?.('img.cs-men-gallery__image')) this.retryImage(event.target);
      }, {capture: true});
      this.listen(this, 'load', event => {
        const image = event.target;
        if (!image.matches?.('img.cs-men-gallery__image')) return;
        clearTimeout(this.imageRetryTimers.get(image));
        this.imageRetryTimers.delete(image);
        delete image.dataset.menImageFailed;
        image.closest('[data-men-select]')?.removeAttribute('data-men-image-failed');
        if (this.viewport.contains(image)) {
          this.viewport.querySelector('[data-men-image-message]')?.remove();
          this.scheduleNextImage();
        }
      }, {capture: true});
      this.listen(this, 'loadeddata', event => {
        if (this.viewport.contains(event.target)) this.scheduleNextImage();
      }, {capture: true});
    }
    checkGalleryImages() {
      if (!this.imageStates) return;
      this.querySelectorAll('img.cs-men-gallery__image').forEach(image => {
        if (!image.complete) return;
        if (!image.naturalWidth) this.retryImage(image);
        else if (this.viewport.contains(image)) this.scheduleNextImage();
      });
    }
    retryImage(image, manual = false) {
      if (!this.imageStates || this.pageHidden || document.hidden || !this.contains(image) || this.imageRetryTimers.has(image)) return;
      let state = this.imageStates.get(image);
      if (!state) {
        state = {attempt: 0, src: image.getAttribute('src'), srcset: image.getAttribute('srcset')};
        this.imageStates.set(image, state);
      }
      if (manual) state.attempt = 0;
      if (state.attempt >= 2) {
        image.dataset.menImageFailed = 'true';
        image.closest('[data-men-select]')?.setAttribute('data-men-image-failed', 'true');
        if (this.viewport.contains(image) && !this.viewport.querySelector('[data-men-image-message]')) {
          const message = document.createElement('div');
          message.className = 'cs-men-gallery__error';
          message.dataset.menImageMessage = '';
          message.setAttribute('role', 'status');
          const text = document.createElement('span');
          text.textContent = this.dataset.menImageError;
          const retry = document.createElement('button');
          retry.type = 'button';
          retry.dataset.menImageRetry = '';
          retry.textContent = this.dataset.menImageRetry;
          message.append(text, retry);
          this.viewport.append(message);
        }
        return;
      }
      state.attempt++;
      const timer = setTimeout(() => {
        this.imageRetryTimers.delete(image);
        if (!this.isConnected || this.pageHidden || document.hidden || !this.contains(image) || (image.complete && image.naturalWidth)) return;
        // Keep all responsive widths and quality settings, including detail zoom.
        const retryURL = source => {
          const url = new URL(source, location.href);
          url.searchParams.set('cs_gallery_retry', String(state.attempt));
          return url.href;
        };
        if (state.srcset) image.srcset = state.srcset.split(',').map(candidate => {
          const parts = candidate.trim().split(/\s+/);
          parts[0] = retryURL(parts[0]);
          return parts.join(' ');
        }).join(', ');
        if (state.src) image.src = retryURL(state.src);
      }, manual ? 0 : state.attempt * 700);
      this.imageRetryTimers.set(image, timer);
    }
    scheduleNextImage() {
      if (!this.imageStates) return;
      clearTimeout(this.prefetchTimer);
      this.prefetchTimer = setTimeout(() => {
        if (!this.isConnected || document.hidden || this.pageHidden || !this.playerVisible() || this.connection?.saveData || matchMedia('(prefers-reduced-data: reduce)').matches || /(^|-)2g$/.test(this.connection?.effectiveType || '') || this.prefetchPending) return;
        const item = this.items[(this.index + 1) % this.items.length];
        const source = item?.content.querySelector('img.cs-men-gallery__image');
        if (item?.dataset.kind !== 'image' || !source || this.prefetched.has(source.src)) return;
        const image = new Image();
        image.fetchPriority = 'low';
        image.decoding = 'async';
        image.sizes = this.dialog.open ? '100vw' : '(max-width: 760px) calc(100vw - 44px), 50vw';
        this.prefetchPending = image;
        this.prefetched.add(source.src);
        image.onload = () => { this.prefetchPending = null; };
        image.onerror = () => { this.prefetchPending = null; this.prefetched.delete(source.src); };
        if (source.srcset) image.srcset = source.srcset;
        image.src = source.src;
      }, 350);
    }
    listen(target, event, handler, options = {}) {
      target.addEventListener(event, handler, {...options, signal: this.controller.signal});
    }
    disconnectedCallback() {
      if (!this.controller) return;
      this.stopPlayer();
      this.stopPreview();
      if (this.mediaFrame) cancelAnimationFrame(this.mediaFrame);
      this.mediaFrame = null;
      this.previewObserver?.disconnect();
      this.playerObserver?.disconnect();
      this.imageRetryTimers?.forEach(timer => clearTimeout(timer));
      this.imageRetryTimers?.clear();
      clearTimeout(this.prefetchTimer);
      this.controller.abort();
      this.playerController?.abort();
      if (this.dialog?.open) this.dialog.close();
      if (this.viewport && this.stage && this.viewport.parentElement !== this.stage) this.stage.prepend(this.viewport);
      this.controller = null;
    }
    observe() {
      if (!('IntersectionObserver' in window)) return;
      if (this.teaser) {
        this.previewObserver = new IntersectionObserver(entries => {
          this.previewVisible = entries.some(entry => entry.target === this.teaser && entry.isIntersecting && entry.intersectionRatio >= .6);
          this.syncPreview();
        }, {threshold: [0, .6]});
        this.previewObserver.observe(this.teaser);
      }
      this.playerObserver = new IntersectionObserver(() => this.syncPlayer(), {threshold: [0, .25, .5, 1]});
      this.playerObserver.observe(this.viewport);
    }
    playerVisible() {
      const rect = this.viewport.getBoundingClientRect();
      const visual = window.visualViewport;
      const top = visual?.offsetTop || 0, left = visual?.offsetLeft || 0;
      const height = visual?.height || innerHeight, width = visual?.width || innerWidth;
      const area = Math.max(0, Math.min(rect.bottom, top + height) - Math.max(rect.top, top)) * Math.max(0, Math.min(rect.right, left + width) - Math.max(rect.left, left));
      const available = Math.min(rect.height, height) * Math.min(rect.width, width);
      return available > 0 && area / available >= .25;
    }
    playerState() {
      const key = this.items[this.index]?.dataset.mediaId || String(this.index);
      if (!this.playerStates.has(key)) this.playerStates.set(key, {paused: false, started: false, blocked: false, failed: false, pending: false, wasVisible: false});
      return this.playerStates.get(key);
    }
    playerAllowed() {
      const state = this.playerState();
      const otherDialog = [...document.querySelectorAll('dialog[open],[role="dialog"][open]')].some(dialog => !dialog.contains(this.viewport));
      return this.isConnected && !document.hidden && !this.pageHidden && !otherDialog && this.playerVisible() && !state.paused && !state.blocked && !state.failed && (state.started || (!this.reduced.matches && !this.connection?.saveData));
    }
    syncPlayer(resetBlocked = false) {
      const video = this.viewport?.querySelector('video');
      if (!video) {
        if (document.hidden || this.pageHidden || !this.playerVisible()) this.stopPlayer();
        return;
      }
      const state = this.playerState();
      const visible = this.playerVisible();
      if (resetBlocked || (visible && !state.wasVisible)) state.blocked = false;
      state.wasVisible = visible;
      if (this.playerAllowed()) this.play(video);
      else this.stopPlayer();
      this.updatePlayerControl();
    }
    updatePlayerControl() {
      const control = this.viewport?.querySelector('[data-men-playback]');
      const video = this.viewport?.querySelector('video');
      if (!control || !video) return;
      const playing = !video.paused && !video.ended;
      control.dataset.state = playing ? 'playing' : 'paused';
      control.setAttribute('aria-label', playing ? control.dataset.pauseLabel : control.dataset.playLabel);
    }
    onClick(event) {
      const button = event.target.closest('button');
      if (!button || button.disabled || !this.contains(button)) return;
      if (button.hasAttribute('data-men-image-retry')) {
        const image = this.viewport.querySelector('img.cs-men-gallery__image');
        if (image) this.retryImage(image, true);
      } else if (button.hasAttribute('data-men-select')) {
        const image = button.querySelector('img.cs-men-gallery__image');
        if (image?.dataset.menImageFailed) this.retryImage(image, true);
        this.select(Number(button.dataset.menSelect));
      }
      else if (button.hasAttribute('data-men-step')) this.select(this.index + Number(button.dataset.menStep));
      else if (button.hasAttribute('data-men-close')) this.dialog.close();
      else if (button.hasAttribute('data-men-playback')) {
        const video = this.viewport.querySelector('video');
        if (!video) return;
        const state = this.playerState();
        state.paused = !video.paused || state.pending;
        if (state.paused) this.stopPlayer();
        else { state.started = true; state.blocked = false; this.syncPlayer(); }
        this.updatePlayerControl();
      } else if (button.hasAttribute('data-men-preview-pause')) {
        const stopped = this.previewStopped();
        this.previewPaused = !stopped;
        if (stopped) { this.previewDenied = false; this.previewOptIn = true; }
        this.syncPreview();
      } else if (button.hasAttribute('data-men-photo') || button.hasAttribute('data-men-expand')) {
        if (Date.now() < (this.swipeUntil || 0)) { event.preventDefault(); return; }
        if (this.dialog.open || !this.items.length) return;
        this.lastTrigger = button;
        this.stopPreview();
        this.dialog.showModal();
        const video = this.viewport.querySelector('video');
        const wasPlaying = video && !video.paused;
        this.querySelector('[data-men-dialog-stage]').append(this.viewport);
        if (wasPlaying) this.play(video);
        this.syncPlayer();
        this.update();
      }
    }
    closeDialog() {
      this.stopPlayer();
      this.stage.prepend(this.viewport);
      if (!this.isConnected || !this.controller) return;
      const selectedThumb = this.thumbs.find(thumb => Number(thumb.dataset.menSelect) === this.index);
      const focusTarget = this.lastTrigger?.isConnected && this.lastTrigger.getClientRects().length ? this.lastTrigger : selectedThumb || this.querySelector('[data-men-expand]');
      focusTarget?.focus({preventScroll: true});
      this.update();
      this.syncPlayer();
    }
    select(index) {
      if (!this.items.length) return;
      clearTimeout(this.prefetchTimer);
      this.stopPlayer();
      this.stopPreview();
      this.index = (index + this.items.length) % this.items.length;
      this.alignSelectedMedia();
      this.render(true);
      this.update();
      this.checkGalleryImages();
      this.revealThumbnail();
    }
    alignSelectedMedia() {
      if (innerWidth > 760 || this.dialog.open || document.fullscreenElement || this.viewport.querySelector('video')?.webkitDisplayingFullscreen) return;
      const rect = this.viewport.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const visual = window.visualViewport;
      let top = visual?.offsetTop || 0;
      const bottom = top + (visual?.height || innerHeight);
      for (const header of document.querySelectorAll('.shopify-section--header, #shopify-section-header, .shopify-section-group-header-group, [data-cs-invitation]')) {
        const style = getComputedStyle(header);
        if (style.position !== 'sticky' && style.position !== 'fixed') continue;
        const visible = header.querySelector('.header') || header;
        const visibleStyle = getComputedStyle(visible);
        const bounds = visible.getBoundingClientRect();
        if (visibleStyle.display === 'none' || visibleStyle.visibility === 'hidden' || visibleStyle.opacity === '0' || !bounds.width || bounds.bottom <= top || bounds.top >= bottom) continue;
        top = Math.max(top, bounds.bottom);
      }
      top += 12;
      if (rect.top >= top - 2 && rect.bottom <= bottom - 10) return;
      const target = Math.max(0, scrollY + rect.top - top);
      if (Math.abs(target - scrollY) < 2) return;
      this.selectionScrolling = true;
      this.selectionScrollTimer = setTimeout(() => this.finishSelectionScroll(), 800);
      window.scrollTo({top: target, behavior: this.reduced.matches ? 'instant' : 'smooth'});
    }
    finishSelectionScroll() {
      if (!this.selectionScrolling) return;
      this.clearSelectionScroll();
      this.syncPlayer();
    }
    clearSelectionScroll() {
      this.selectionScrolling = false;
      clearTimeout(this.selectionScrollTimer);
    }
    render(autoplay) {
      const item = this.items[this.index];
      if (!item) return;
      this.playerController?.abort();
      this.playerController = new AbortController();
      this.viewport.replaceChildren(item.content.cloneNode(true));
      const video = this.viewport.querySelector('video');
      if (video) {
        video.muted = true;
        video.playsInline = true;
        video.defaultMuted = true;
        video.controls = false;
        video.removeAttribute('controls');
        video.loop = true;
        video.addEventListener('play', () => {
          if (this.items[this.index] !== item || !this.playerAllowed()) { this.pauseVideo(video); return; }
          this.updatePlayerControl();
          this.stopPreview();
          this.dispatchEvent(new CustomEvent('media:play', {bubbles: true}));
        }, {signal: this.playerController.signal});
        video.addEventListener('pause', () => {
          if (this.systemPauses.has(video)) this.systemPauses.delete(video);
          else if (this.viewport.querySelector('video') === video && this.playerVisible() && !video.seeking) this.playerState().paused = true;
          this.updatePlayerControl();
        }, {signal: this.playerController.signal});
        video.addEventListener('error', () => {
          if (this.items[this.index] !== item || this.viewport.querySelector('[data-men-video-error]')) return;
          this.playerState().failed = true;
          this.updatePlayerControl();
          const message = document.createElement('p');
          message.className = 'cs-men-gallery__error';
          message.dataset.menVideoError = '';
          message.setAttribute('role', 'status');
          message.textContent = this.dataset.unavailable || '';
          this.viewport.append(message);
        }, {signal: this.playerController.signal});
        if (autoplay) { const state = this.playerState(); state.started = true; state.paused = false; state.blocked = false; }
        this.syncPlayer();
      }
      const iframe = this.viewport.querySelector('iframe');
      if (iframe) iframe.addEventListener('load', () => {
        if (document.hidden || this.items[this.index] !== item) this.stopPlayer();
      }, {signal: this.playerController.signal});
      if (iframe && !autoplay) {
        const source = new URL(iframe.src, location.href);
        source.searchParams.set('autoplay', '0');
        iframe.src = source.href;
      }
    }
    play(video) {
      if (!this.playerAllowed()) return;
      const state = this.playerState();
      if (state.pending || !video.paused) return;
      if (video.ended) video.currentTime = 0;
      const epoch = ++this.playEpoch;
      state.pending = true;
      video.autoplay = true;
      const resolved = () => {
        if (epoch !== this.playEpoch) {
          if (!this.playerAllowed() || this.viewport.querySelector('video') !== video) { video.autoplay = false; this.pauseVideo(video); }
          return;
        }
        state.pending = false;
        if (!this.playerAllowed()) { video.autoplay = false; this.pauseVideo(video); }
        this.updatePlayerControl();
      };
      const rejected = error => {
        if (epoch !== this.playEpoch) return;
        state.pending = false;
        video.autoplay = false;
        if (error?.name !== 'AbortError') state.blocked = true;
        this.updatePlayerControl();
      };
      try {
        const pending = video.play();
        if (pending?.then) pending.then(resolved).catch(rejected);
        else resolved();
      } catch (error) { rejected(error); }
    }
    pauseVideo(video) {
      if (video.paused) return;
      this.systemPauses.add(video);
      video.pause();
    }
    stopPlayer() {
      this.clearSelectionScroll();
      this.playEpoch++;
      if (this.items?.length) this.playerState().pending = false;
      this.viewport?.querySelectorAll('video').forEach(video => { video.autoplay = false; this.pauseVideo(video); });
      this.updatePlayerControl();
      this.viewport?.querySelectorAll('model-media').forEach(model => model.pause?.());
      this.viewport?.querySelectorAll('iframe').forEach(iframe => {
        try {
          const source = new URL(iframe.src, location.href);
          const youtube = /(^|\.)youtube(-nocookie)?\.com$/.test(source.hostname);
          const vimeo = /(^|\.)vimeo\.com$/.test(source.hostname);
          if (youtube) iframe.contentWindow?.postMessage(JSON.stringify({event: 'command', func: 'pauseVideo', args: []}), source.origin);
          if (vimeo) iframe.contentWindow?.postMessage(JSON.stringify({method: 'pause'}), source.origin);
        } catch (_) {}
      });
    }
    previewStopped() {
      return this.previewPaused || this.previewDenied || !this.previewOptIn && (this.reduced.matches || this.connection?.saveData);
    }
    previewAllowed() {
      return this.isConnected && !document.hidden && !this.previewStopped() && this.items[this.index]?.dataset.kind === 'image' && !document.querySelector('dialog[open]');
    }
    stopPreview() {
      this.previewEpoch++;
      this.previewPending = false;
      if (!this.teaser) return;
      this.teaser.pause();
      this.teaser.closest('[data-men-select]').classList.remove('is-previewing');
    }
    syncPreview() {
      if (!this.teaser) return;
      if (!this.previewVisible || !this.previewAllowed()) this.stopPreview();
      else if (this.teaser.paused && !this.previewPending) {
        if (!this.teaser.getAttribute('src')) this.teaser.src = this.teaser.dataset.previewSrc;
        this.teaser.muted = true;
        const epoch = ++this.previewEpoch;
        this.previewPending = true;
        this.teaser.play().then(() => {
          if (epoch !== this.previewEpoch) return;
          this.previewPending = false;
          if (this.previewVisible && this.previewAllowed()) this.teaser.closest('[data-men-select]').classList.add('is-previewing');
          else this.stopPreview();
        }).catch(error => {
          if (epoch !== this.previewEpoch) return;
          this.previewPending = false;
          if (error.name !== 'AbortError') { this.previewDenied = true; this.updatePreviewControl(); }
        });
      }
      this.updatePreviewControl();
    }
    updatePreviewControl() {
      if (!this.previewControl) return;
      const stopped = this.previewStopped();
      const label = stopped ? this.previewControl.dataset.playLabel : this.previewControl.dataset.pauseLabel;
      this.previewControl.setAttribute('aria-label', label);
      this.previewControl.title = label;
      this.previewControl.setAttribute('aria-pressed', String(!!stopped));
      this.previewControl.dataset.state = stopped ? 'paused' : 'playing';
      this.previewControl.disabled = this.items[this.index]?.dataset.kind !== 'image';
    }
    update() {
      const count = `${String(this.items.length ? this.index + 1 : 0).padStart(2, '0')} / ${String(this.items.length).padStart(2, '0')}`;
      this.querySelector('[data-men-count]').textContent = count;
      this.querySelector('[data-men-dialog-count]').textContent = `${count} · ${this.items[this.index]?.dataset.label || ''}`;
      const photo = this.viewport.querySelector('[data-men-photo] img');
      if (photo) photo.sizes = this.dialog.open ? '100vw' : '(max-width: 760px) calc(100vw - 44px), 50vw';
      this.thumbs.forEach(thumb => thumb.setAttribute('aria-pressed', String(Number(thumb.dataset.menSelect) === this.index)));
      this.syncPreview();
    }
    revealThumbnail() {
      if (!this.rail) return;
      const behavior = this.reduced.matches ? 'instant' : 'smooth';
      if (!this.index) { this.rail.scrollTo({left: 0, behavior}); return; }
      const thumb = this.thumbs.find(candidate => Number(candidate.dataset.menSelect) === this.index);
      if (!thumb) return;
      const outer = this.rail.getBoundingClientRect();
      const inner = thumb.getBoundingClientRect();
      if (inner.left < outer.left) this.rail.scrollBy({left: inner.left - outer.left, behavior});
      else if (inner.right > outer.right) this.rail.scrollBy({left: inner.right - outer.right, behavior});
    }
  }
  customElements.define('cs-men-gallery', CsMenGallery);
})();

